import React, { useState } from 'react';
import { Check, X, BookOpen, Battery, Heart, ShieldCheck, Gift, ChevronLeft, ChevronRight, Brain, Moon, MessageCircle } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { CTAButton } from '../components/CTAButton';
import { TestimonialCard } from '../components/TestimonialCard';
import { FAQAccordion } from '../components/FAQAccordion';
import { SocialProofPopup } from '../components/SocialProofPopup';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/8HwGXiVdSH14Bnvw8M3uaq/Mockup_-_De_Alerta_a_Calma_(SIN_FONDO).png';
const BONUS_1_IMAGE = 'https://cdn.magicpatterns.com/uploads/jkbcEbn8utzJ4yZVSaxDQA/Mockup_-_Bono_1.png';
const BONUS_2_IMAGE = 'https://cdn.magicpatterns.com/uploads/kXc3M5wCydP356nM35Gnuh/Mockup_-_Bono_2.png';
const BONUS_3_IMAGE = 'https://cdn.magicpatterns.com/uploads/qR5T7dfWxWPmPYNWYg44a1/Moclup_-_Bono_3.png';
// Empty preview pages as requested
const PREVIEW_PAGES: string[] = [];
const SECTION_LINK = '#compra';
export function SalesPage() {
  const [currentPage, setCurrentPage] = useState(0);
  // Only show navigation if there are pages
  const showNavigation = PREVIEW_PAGES.length > 1;
  const nextPage = () => {
    if (!showNavigation) return;
    setCurrentPage(prev => (prev + 1) % PREVIEW_PAGES.length);
  };
  const prevPage = () => {
    if (!showNavigation) return;
    setCurrentPage(prev => (prev - 1 + PREVIEW_PAGES.length) % PREVIEW_PAGES.length);
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-34">
      <CountdownTimer data-id="element-35" />
      <SocialProofPopup data-id="element-36" />

      {/* HERO SECTION */}
      <section className="pt-8 pb-12 px-4 md:pt-12 md:pb-20 bg-gradient-to-b from-white to-gray-50" data-id="element-37">
        <div className="max-w-4xl mx-auto text-center" data-id="element-38">
          {/* Blue pill container for buyer persona text */}
          <div className="inline-flex items-center justify-center bg-blue-100 text-blue-700 px-6 py-2 rounded-full mb-6" data-id="element-39">
            <span className="text-sm md:text-base font-medium uppercase tracking-wide" data-id="element-40">
              🧠 PARA MUJERES QUE VIVEN EN MODO ALERTA CONSTANTE
            </span>
          </div>

          <h1 className="text-[28px] md:text-5xl font-black tracking-tight text-gray-900 mb-8 leading-[1.15]" data-id="element-41">
            Recuperá la calma real sin luchar contra tu mente en 30 días
          </h1>

          <div className="mb-6" data-id="element-42">
            <a href={SECTION_LINK} data-id="element-43">
              <img src={PRODUCT_IMAGE} alt="MegaPack De Alerta a Calma" className="w-full max-w-lg mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300 cursor-pointer" data-id="element-44" />
            </a>
          </div>

          <p className="text-xl md:text-2xl font-bold text-gray-900 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-45">
            El sistema práctico diseñado para mujeres que viven en{' '}
            <span className="bg-yellow-300 px-1" data-id="element-46">modo alerta constante</span> y
            quieren{' '}
            <span className="bg-yellow-300 px-1" data-id="element-47">
              estabilidad emocional real
            </span>
            .
          </p>

          <CTAButton data-id="element-48">👉 SÍ, QUIERO RECUPERAR MI CALMA</CTAButton>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-49">
        <div className="max-w-3xl mx-auto" data-id="element-50">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-51">
            Si eres "funcional", pero por dentro estás agotada… déjame
            preguntarte algo honesto
          </h2>

          <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-52">
            <p className="font-medium" data-id="element-53">¿Te pasa que tu mente no para nunca?</p>
            <p data-id="element-54">¿Te acuestas cansada pero tu cabeza sigue activa?</p>
            <p data-id="element-55">¿Sobrepiensas conversaciones durante horas?</p>
            <p data-id="element-56">¿Te activas demasiado rápido en discusiones?</p>
            <p data-id="element-57">¿Te sientes productiva… pero emocionalmente exhausta?</p>

            <p className="text-xl font-bold text-gray-900 mt-8" data-id="element-58">
              No estás rota. No eres débil. Y no te falta disciplina.
            </p>
            <p data-id="element-59">Pero algo sí está pasando.</p>
            <p className="text-center font-bold text-2xl mt-8 border-l-4 border-[#dc2626] pl-4 py-2 bg-red-50" data-id="element-60">
              Y no está en tu mente.
            </p>
          </div>

          <div className="mt-12" data-id="element-61">
            <CTAButton data-id="element-62">QUIERO SALIR DEL MODO ALERTA</CTAButton>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 px-4 bg-gray-900 text-white" data-id="element-63">
        <div className="max-w-4xl mx-auto" data-id="element-64">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-yellow-400" data-id="element-65">
            El problema que nadie te explica
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-66">
            <div className="space-y-4" data-id="element-67">
              <p className="text-xl font-medium mb-6" data-id="element-68">
                Si esto te pasa, probablemente te resulte familiar:
              </p>
              <ul className="space-y-4" data-id="element-69">
                {['Vives en hipervigilancia constante', 'Te cuesta relajarte incluso cuando todo está bien', 'Te activas con facilidad ante cualquier tensión', 'Te cuesta dormir profundamente', 'Te sientes en "modo supervivencia" sin razón clara'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-70">
                    <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-71" />
                    <span className="text-lg" data-id="element-72">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-gray-800 p-8 rounded-xl border border-gray-700" data-id="element-73">
              <p className="text-lg mb-6" data-id="element-74">
                Lo peor es esa sensación interna de:
              </p>
              <p className="text-2xl font-bold italic text-gray-300 mb-8" data-id="element-75">
                "Estoy haciendo todo lo posible… pero mi mente no se apaga."
              </p>

              <div className="space-y-2 font-bold text-red-400 text-xl" data-id="element-76">
                <p data-id="element-77">❌ No es que estés pensando demasiado.</p>
                <p data-id="element-78">❌ No es que necesites más fuerza de voluntad.</p>
              </div>

              <p className="mt-6 text-gray-300" data-id="element-79">
                La ansiedad no se resuelve pensando más. Se regula desde el
                cuerpo. Tu sistema nervioso está en activación basal crónica. Y
                nadie te enseñó a regularlo.
              </p>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-80">
            <p className="text-2xl font-bold text-[#22c55e]" data-id="element-81">
              👉 De Alerta a Calma™ nace para cortar ese ciclo.
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-82">
        <div className="max-w-5xl mx-auto" data-id="element-83">
          <div className="text-center mb-16" data-id="element-84">
            <span className="bg-green-100 text-green-800 px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide" data-id="element-85">
              ✅ LA SOLUCIÓN
            </span>
            <h2 className="text-4xl md:text-5xl font-black mt-6 mb-4" data-id="element-86">
              MegaPack · De Alerta a Calma™
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-87">
              Un sistema completo en 6 ebooks prácticos diseñado para entrenar
              tu sistema nervioso y ayudarte a pasar de alerta constante a
              estabilidad emocional sostenible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-88">
            <div className="bg-green-50 p-8 rounded-2xl border border-green-100" data-id="element-89">
              <h3 className="text-2xl font-bold mb-6" data-id="element-90">
                Más de 450 páginas aplicables:
              </h3>
              <ul className="space-y-4" data-id="element-91">
                {['Sin teoría pesada', 'Sin espiritualidad confusa', 'Sin frases motivacionales vacías', 'Solo herramientas prácticas'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-92">
                    <Check className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-93" />
                    <span className="text-lg font-medium text-gray-800" data-id="element-94">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-col justify-center items-center text-center p-8 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-95">
              <div className="space-y-6 text-xl font-bold text-gray-800" data-id="element-96">
                <div className="flex items-center gap-4" data-id="element-97">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-98">
                    1
                  </span>
                  <span className="text-left" data-id="element-99">📂 Abres el material</span>
                </div>
                <div className="flex items-center gap-4" data-id="element-100">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-101">
                    2
                  </span>
                  <span className="text-left" data-id="element-102">
                    👆 Identificas tu patrón de activación
                  </span>
                </div>
                <div className="flex items-center gap-4" data-id="element-103">
                  <span className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center text-sm flex-shrink-0" data-id="element-104">
                    3
                  </span>
                  <span className="text-left" data-id="element-105">
                    🧘‍♀️ Aplicás los protocolos en tu vida real
                  </span>
                </div>
              </div>
              <p className="mt-8 text-lg text-gray-600 italic" data-id="element-106">
                Sin luchar contra tu mente. Sin forzarte a "pensar positivo".
                Sin empezar de cero cada semana.
              </p>
            </div>
          </div>

          <CTAButton data-id="element-107">👉 QUIERO EMPEZAR AHORA</CTAButton>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-108">
        <div className="max-w-5xl mx-auto" data-id="element-109">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-110">
            📘 ¿Qué incluye exactamente el MegaPack?
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12" data-id="element-111">
            🔹 6 TOMOS PRINCIPALES: Un entrenamiento progresivo de regulación
            nerviosa
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-112">
            {[{
            icon: '📘',
            title: 'Tomo 1 – Entender para Calmar',
            desc: 'Deja de culparte y entiende cómo funciona tu activación.'
          }, {
            icon: '📘',
            title: 'Tomo 2 – Salir del Modo Alerta',
            desc: 'Reduce hipervigilancia y tensión constante con protocolos de 3 minutos.'
          }, {
            icon: '📘',
            title: 'Tomo 3 – Activar la Calma Corporal',
            desc: 'Entrena tu cuerpo a entrar en estado de seguridad real.'
          }, {
            icon: '📘',
            title: 'Tomo 4 – Regular Emociones Intensas',
            desc: 'Aprende a sostener conflictos sin explotar ni congelarte.'
          }, {
            icon: '📘',
            title: 'Tomo 5 – Construir Resiliencia Diaria',
            desc: 'Microhábitos para evitar recaídas y sostener estabilidad.'
          }, {
            icon: '📘',
            title: 'Tomo 6 – Calma Sostenida',
            desc: 'Integra una identidad regulada, segura y consciente.'
          }].map((tomo, i) => <a href={SECTION_LINK} key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer" data-id="element-113">
                <div className="text-4xl mb-4" data-id="element-114">{tomo.icon}</div>
                <h3 className="text-xl font-bold mb-2" data-id="element-115">{tomo.title}</h3>
                <p className="text-gray-600" data-id="element-116">{tomo.desc}</p>
              </a>)}
          </div>

          <p className="text-center text-lg font-medium mt-12 text-gray-700" data-id="element-117">
            👉 Todo organizado para que sepas qué hacer y cuándo hacerlo.
          </p>
        </div>
      </section>

      {/* BEFORE VS AFTER */}
      <section className="py-16 px-4 bg-white" data-id="element-118">
        <div className="max-w-4xl mx-auto" data-id="element-119">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-120">
            Antes vs Después
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-121">
            <div className="bg-red-50 p-8 rounded-2xl border border-red-100" data-id="element-122">
              <h3 className="text-xl font-bold text-red-800 mb-6 text-center" data-id="element-123">
                Antes de De Alerta a Calma™
              </h3>
              <ul className="space-y-4" data-id="element-124">
                {['Mente acelerada', 'Hipervigilancia constante', 'Reacciones impulsivas', 'Agotamiento interno', 'Dificultad para dormir', 'Sensación de inseguridad emocional'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-125">
                    <X className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-126" />
                    <span className="text-gray-700" data-id="element-127">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg transform md:-translate-y-4" data-id="element-128">
              <h3 className="text-xl font-bold text-green-800 mb-6 text-center" data-id="element-129">
                Después de De Alerta a Calma™
              </h3>
              <ul className="space-y-4" data-id="element-130">
                {['Calma corporal real', 'Respuestas conscientes', 'Sueño más profundo', 'Conflictos sin explosión', 'Energía emocional estable', 'Seguridad interna sostenida'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-131">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-132" />
                    <span className="font-bold text-gray-800" data-id="element-133">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-16 px-4 bg-[#0f172a] text-white" data-id="element-134">
        <div className="max-w-5xl mx-auto" data-id="element-135">
          <div className="text-center mb-12" data-id="element-136">
            <span className="bg-yellow-400 text-black px-4 py-1 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-137">
              🎁 REGALOS ESPECIALES
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mt-6 mb-4" data-id="element-138">
              BONOS INCLUIDOS HOY
            </h2>
            <p className="text-xl text-yellow-400 font-bold" data-id="element-139">
              ¡GRATIS POR HOY!
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-140">
            {/* Bonus 1 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-141">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-142">
                <img src={BONUS_1_IMAGE} alt="Bono 1 - Sueño en Calma" className="w-full h-full object-contain drop-shadow-xl" data-id="element-143" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-144">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-145">
                🌙 Bono #1 – Sueño en Calma
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-146">
                Protocolo nocturno para apagar la mente y regular activación
                antes de dormir.
              </p>
            </a>

            {/* Bonus 2 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-147">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-148">
                <img src={BONUS_2_IMAGE} alt="Bono 2 - Cortar el Sobrepensamiento" className="w-full h-full object-contain drop-shadow-xl" data-id="element-149" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-150">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-151">
                🧠 Bono #2 – Cortar el Sobrepensamiento
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-152">
                Plan práctico de 7 días para salir del bucle mental ansioso.
              </p>
            </a>

            {/* Bonus 3 */}
            <a href={SECTION_LINK} className="bg-[#1e293b] rounded-xl p-6 border border-[#334155] flex flex-col items-center text-center hover:border-yellow-400 transition-colors cursor-pointer" data-id="element-153">
              <div className="w-full aspect-square max-w-[270px] mb-6 flex items-center justify-center rounded-lg bg-[#1e293b]" data-id="element-154">
                <img src={BONUS_3_IMAGE} alt="Bono 3 - Conversaciones sin Ansiedad" className="w-full h-full object-contain drop-shadow-xl" data-id="element-155" />
              </div>
              <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-xs font-bold mb-3" data-id="element-156">
                GRATIS HOY
              </span>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-157">
                💬 Bono #3 – Conversaciones sin Ansiedad
              </h3>
              <p className="text-gray-300 text-sm" data-id="element-158">
                Aprende a hablar sin activarte, explotar o congelarte.
              </p>
            </a>
          </div>

          <div className="mt-12 text-center space-y-2 text-lg font-medium" data-id="element-159">
            <p data-id="element-160">👉 Los tomos te enseñan a regular.</p>
            <p data-id="element-161">👉 Los bonos evitan que recaigas.</p>
            <p className="text-yellow-400 font-bold text-xl mt-4" data-id="element-162">
              Eso es lo que lo vuelve sostenible.
            </p>
          </div>

          <div className="mt-12" data-id="element-163">
            <CTAButton data-id="element-164">QUIERO MIS BONOS AHORA</CTAButton>
          </div>
        </div>
      </section>

      {/* OFFER STACK SECTION */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-gray-900 to-gray-800" data-id="element-165">
        <div className="max-w-4xl mx-auto" data-id="element-166">
          {/* Big Title */}
          <div className="text-center mb-10" data-id="element-167">
            <span className="bg-red-500 text-white px-4 py-2 rounded-full font-bold text-sm uppercase tracking-wide animate-pulse inline-block mb-4" data-id="element-168">
              ⏰ OFERTA ESPECIAL
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase leading-tight" data-id="element-169">
              Oferta por
              <br data-id="element-170" />
              <span className="text-yellow-400" data-id="element-171">Tiempo Limitado</span>
            </h2>
          </div>

          {/* Offer Box */}
          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-2xl" data-id="element-172">
            <h3 className="text-2xl md:text-3xl font-black text-center mb-8 text-gray-900" data-id="element-173">
              📦 Lo que recibes hoy
            </h3>

            <div className="space-y-4 mb-10" data-id="element-174">
              {['6 tomos completos (450+ páginas)', 'Bono "Sueño en Calma"', 'Bono "Cortar el Sobrepensamiento"', 'Bono "Conversaciones sin Ansiedad"', 'Acceso digital inmediato', 'Acceso ilimitado'].map((item, i) => <div key={i} className="flex items-center gap-3 text-lg md:text-xl border-b border-gray-200 pb-3 last:border-0" data-id="element-175">
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0" data-id="element-176" />
                  <span className="font-bold text-gray-800" data-id="element-177">{item}</span>
                </div>)}
            </div>

            <div className="text-center mb-8" data-id="element-178">
              <p className="text-gray-500 text-xl mb-2" data-id="element-179">
                Valor estimado:{' '}
                <span className="line-through decoration-red-500 decoration-2" data-id="element-180">
                  $49.990 ARS
                </span>
              </p>
              <p className="text-5xl md:text-7xl font-black text-[#22c55e]" data-id="element-181">
                🔥 $19.990 ARS
              </p>
              <p className="text-gray-600 mt-2 font-medium" data-id="element-182">Solo por hoy</p>
            </div>

            <div className="flex flex-col items-center justify-center w-full" data-id="element-183">
              <a href="https://checkout.tumundoglobal.online/cart/45134986313919:1" className="relative group flex items-center justify-center gap-2 w-full md:max-w-2xl px-6 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1 text-xl md:text-2xl font-bold uppercase tracking-wide" data-id="element-184">
                👉 OBTENER MI MEGAPACK AHORA
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 md:w-7 md:h-7" data-id="element-185">
                  <path d="M5 12h14" data-id="element-186" />
                  <path d="m12 5 7 7-7 7" data-id="element-187" />
                </svg>
              </a>
            </div>
          </div>

          {/* GUARANTEE - Outside the offer box */}
          <div className="mt-10 flex flex-col md:flex-row items-center gap-6 bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20" data-id="element-188">
            <ShieldCheck className="w-16 h-16 text-yellow-400 flex-shrink-0" data-id="element-189" />
            <div className="text-center md:text-left" data-id="element-190">
              <h3 className="text-xl font-bold mb-2 text-white" data-id="element-191">
                Garantía de satisfacción
              </h3>
              <p className="text-gray-300" data-id="element-192">
                Sin suscripciones. Sin pagos recurrentes. Sin compromisos.
                Accedes hoy y es tuyo para siempre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PREVIEW PAGES CAROUSEL - Structure kept but content empty */}
      <section className="py-16 px-4 bg-white" data-id="element-193">
        <div className="max-w-4xl mx-auto" data-id="element-194">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4" data-id="element-195">
            📖 Revisa algunas páginas
          </h2>
          <p className="text-center text-gray-600 mb-10" data-id="element-196">
            Mira una muestra del contenido práctico que recibirás:
          </p>

          {/* Carousel Placeholder */}
          <div className="relative min-h-[300px] bg-gray-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300" data-id="element-197">
            <p className="text-gray-500 font-medium" data-id="element-198">
              Las páginas de muestra se cargarán próximamente.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-199">
        <div className="max-w-6xl mx-auto" data-id="element-200">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-201">
            Lo que dicen quienes ya lo probaron
          </h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-202">
            <TestimonialCard name="Sofía" age={34} location="Buenos Aires" quote="Sentía que vivía corriendo, incluso sentada en el sillón. Con los ejercicios del Tomo 2 logré bajar esa alerta constante. Es increíble." data-id="element-203" />
            <TestimonialCard name="Mariana" age={41} location="Córdoba" quote="Llevo años en terapia y me ayuda, pero esto me dio herramientas para el momento exacto en que siento que voy a explotar. Muy práctico." data-id="element-204" />
            <TestimonialCard name="Lucía" age={29} location="Rosario" quote="El bono de sobrepensamiento es oro. Dejé de dar vueltas en la cama pensando en lo que dije o no dije. Duermo mucho mejor." data-id="element-205" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-6" data-id="element-206">
            <TestimonialCard name="Carolina" age={38} location="Mendoza" quote="No es el típico libro de 'piensa positivo'. Te explica qué le pasa a tu cuerpo y cómo calmarlo. Me siento mucho más segura." data-id="element-207" />
            <TestimonialCard name="Valentina" age={45} location="La Plata" quote="Mis hijos notaron el cambio. Ya no grito por cualquier cosa, aprendí a regularme antes de reaccionar. Gracias por esto." data-id="element-208" />
            <TestimonialCard name="Ana" age={32} location="Santa Fe" quote="Pensé que era mi personalidad ser ansiosa. Entendí que era mi sistema nervioso pidiendo ayuda. Me cambió la vida." data-id="element-209" />
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 px-4 bg-white" data-id="element-210">
        <div className="max-w-3xl mx-auto" data-id="element-211">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-212">
            Preguntas frecuentes
          </h2>
          <FAQAccordion data-id="element-213" />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-gray-900 text-white text-center" data-id="element-214">
        <div className="max-w-3xl mx-auto" data-id="element-215">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-216">
            🧠 Regularte puede ser más simple
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed" data-id="element-217">
            No necesitas pensar más. No necesitas ser más fuerte.
            <br className="hidden md:block" data-id="element-218" />
            <span className="md:hidden" data-id="element-219"> </span>
            No necesitas controlarte todo el tiempo.
          </p>
          <p className="text-xl text-gray-300 mb-10 -mt-4 leading-relaxed" data-id="element-220">
            Necesitas entrenar tu sistema nervioso.
          </p>

          <a href={SECTION_LINK} className="inline-flex flex-col items-center justify-center px-12 py-5 md:py-6 bg-[#22c55e] hover:bg-[#16a34a] text-white text-center rounded-xl shadow-xl hover:shadow-2xl transform transition-all duration-200 hover:-translate-y-1 border-b-4 border-[#15803d] active:border-b-0 active:translate-y-1" data-id="element-221">
            <span className="text-lg md:text-xl font-bold uppercase tracking-wide" data-id="element-222">
              Acceder ahora
            </span>
            <span className="text-xl md:text-2xl font-black uppercase tracking-wide" data-id="element-223">
              al MegaPack
            </span>
          </a>

          <p className="mt-8 text-yellow-400 font-bold text-lg tracking-wide" data-id="element-224">
            LA CALMA NO SE PIENSA. SE ENTRENA.
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 bg-black text-gray-500 text-center text-sm" data-id="element-225">
        <p data-id="element-226">© 2024 De Alerta a Calma. Todos los derechos reservados.</p>
        <p className="mt-2" data-id="element-227">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}